import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Image from "next/image";
import { configuration } from "../../helpers/config";
import { toggleForm } from "../../redux/ActionCreator";
import useIsMobile from "../../hooks/useIsMobile";
import useScrollDirection from "../../hooks/useScrollDirection";
import BrochureSelectionPopup from "../forms/dynamic-form/CtaSelectionPopup";
import axios from "axios";
import { useRouter } from 'next/router';

const AUTO_COLLAPSE_DELAY = 2000;

const formIconOptions = [
  { value: "testdrive", label: "Test Drive" },
  { value: "brochure", label: "Brochure" },
  { value: "inquiry", label: "Inquiry" },
  { value: "offer", label: "Offer" },
  { value: "service", label: "Service" },
  { value: "quotation", label: "Quotation" },
  { value: "configurator", label: "Configurator" },
  { value: "newsletter", label: "News Letter" },
  { value: "pricelist", label: "Price List" },
  { value: "contactus", label: "Contact Us" },
  { value: "socialnetwork", label: "Social Network" },
  { value: "calculator", label: "Calculator" },
  { value: "car", label: "Car" },
  { value: "bike", label: "Bike" },
  { value: "finddealer", label: "Find Dealer" },
];

export const formNameToLabelMap = {
  testdrive: `${configuration?.IMG_URL}icon-library/TestDrive/testdrive_search_hands_free-white.svg`,
  brochure: `${configuration?.IMG_URL}icon-library/Brochure/brochure_two_pager-white.svg`,
  inquiry: `${configuration?.IMG_URL}icon-library/Inquiry/inquiry_support_agent-white.svg`,
  offer: `${configuration?.IMG_URL}icon-library/Offer/offer_car_tag-white.svg`,
  service: `${configuration?.IMG_URL}icon-library/Service/service_build-white.svg`,
  quotation: `${configuration?.IMG_URL}icon-library/Quotation/request_quote-white.svg`,
  configurator: `${configuration?.IMG_URL}icon-library/Configurator/configurator-white.svg`,
  newsletter: `${configuration?.IMG_URL}icon-library/NewsLetter/breaking_news-white.svg`,
  pricelist: `${configuration?.IMG_URL}icon-library/PriceList/payments-white.svg`,
  contactus: `${configuration?.IMG_URL}icon-library/Contactus/contactus_call-white.svg`,
  socialnetwork: `${configuration?.IMG_URL}icon-library/SocialNetwork/social_email-white.svg`,
  calculator: `${configuration?.IMG_URL}icon-library/Calculator/calculate-white.svg`,
  car: `${configuration?.IMG_URL}icon-library/Car/directions_car-white.svg`,
  bike: `${configuration?.IMG_URL}icon-library/Bike/motorcycle-white.svg`,
  finddealer: `${configuration?.IMG_URL}icon-library/FindDealer/finddealer_distance-white.svg`,
};
export const formNameToLabelMapDarkMode = {
  testdrive: `${configuration?.IMG_URL}icon-library/TestDrive/testdrive_search_hands_free-blue.svg`,
  brochure: `${configuration?.IMG_URL}icon-library/Brochure/brochure_two_pager-blue.svg`,
  inquiry: `${configuration?.IMG_URL}icon-library/Inquiry/inquiry_support_agent-blue.svg`,
  offer: `${configuration?.IMG_URL}icon-library/Offer/offer_car_tag-blue.svg`,
  service: `${configuration?.IMG_URL}icon-library/Service/service_build-blue.svg`,
  quotation: `${configuration?.IMG_URL}icon-library/Quotation/request_quote-blue.svg`,
  configurator: `${configuration?.IMG_URL}icon-library/Configurator/configurator-blue.svg`,
  newsletter: `${configuration?.IMG_URL}icon-library/NewsLetter/breaking_news-blue.svg`,
  pricelist: `${configuration?.IMG_URL}icon-library/PriceList/payments-blue.svg`,
  contactus: `${configuration?.IMG_URL}icon-library/Contactus/contactus_call-blue.svg`,
  socialnetwork: `${configuration?.IMG_URL}icon-library/SocialNetwork/social_email-blue.svg`,
  calculator: `${configuration?.IMG_URL}icon-library/Calculator/calculate-blue.svg`,
  car: `${configuration?.IMG_URL}icon-library/Car/directions_car-blue.svg`,
  bike: `${configuration?.IMG_URL}icon-library/Bike/motorcycle-blue.svg`,
  finddealer: `${configuration?.IMG_URL}icon-library/FindDealer/finddealer_distance-blue.svg`,
};

const FloatingMenu = (props) => {
  const { data } = props;


 const router = useRouter();
 const { brand } = router.query;

  const scrollDirection = useScrollDirection();
  const isMobile = useIsMobile();

  const sidebarRef = useRef(null);
  const isMini = useRef(true);
  const popupRef = useRef(null);
  const dispatch = useDispatch();

  const [isVisible, setIsVisible] = useState(false);
  const [activeMobileIndex, setActiveMobileIndex] = useState(null);
  const [popupPosition, setPopupPosition] = useState({
    left: 0,
    alignment: "center",
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBrochurePopup, setShowBrochurePopup] = useState(false);
  const [models, setModels] = useState([]);
  const [floatSelectedModel, setFloatSelectedModel] = useState(null);

  useEffect(() => {
    if (!isMobile) {
      setIsExpanded(true);
      isMini.current = false;

      const timer = setTimeout(() => {
        setIsExpanded(false);
        isMini.current = true;
      }, AUTO_COLLAPSE_DELAY);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const toggleSidebar = () => {
    const sidebar = sidebarRef.current;

    if (isMini.current) {
      sidebar.classList.add("expanded");
    } else {
      sidebar.classList.remove("expanded");
    }

    isMini.current = !isMini.current;
  };

  const getIconUrl = (item) => {
    let finalUrl = "";
    if (data?.theme_color === "dark" && item?.light_icon_url) {
      finalUrl = `${configuration.IMG_URL}${item?.light_icon_url}`;
      return finalUrl;
    }

    if (data?.theme_color === "light" && item?.dark_icon_url) {
      finalUrl = `${configuration.IMG_URL}${item?.dark_icon_url}`;
      return finalUrl;
    }

    if (data?.theme_color === "dark") {
      if (formNameToLabelMap[item?.form_icon]) {
        finalUrl = formNameToLabelMap[item?.form_icon];
        return finalUrl;
      }
    }

    if (data?.theme_color === "light") {
      if (formNameToLabelMapDarkMode[item?.form_icon]) {
        finalUrl = formNameToLabelMapDarkMode[item?.form_icon];
        return finalUrl;
      }
    }

    return null;
  };

  const onClickHandler = async (ctaData) => {
    if (ctaData?.cta_action === "form") {
      if (ctaData.isBrochureDownload === true) {
        const directDownloadConfig = {
          directDownload: true,
          selectmodel: null,
          brochure: null,
          customDocument: null,
          ctaId: null,
        };
        localStorage.setItem("ctaData", JSON.stringify(directDownloadConfig));
      }
      dispatch(toggleForm(ctaData?.form_name));
    } else if (ctaData?.cta_action === "link") {
      if (ctaData?.cta_target === "same_tab") {
        window.location.href = ctaData?.cta_link;
      } else if (ctaData?.cta_target === "new_tab") {
        window.open(ctaData?.cta_link, "_blank");
      }
    } else if (ctaData?.cta_action === "brochure") {
      const brochureConfig = {
        showBrochureSelection: true,
        modelName: ctaData?.brochure_model,
        modelBrochureUrl: null,
        accessoryBrochureUrl: null,
        hasModelBrochure: false,
        hasAccessoryBrochure: false,
        defaultSelection: "model",
        bothScenario: true,
        preSelectedModel: brand ? brand : ctaData?.brochure_model,
        preSelectedModelId: null,
        defaultBrochureType: "model",
      };
      localStorage.setItem(
        "brochureSelectionConfig",
        JSON.stringify(brochureConfig)
      );
       const res = await axios.get(`${configuration?.APP_URL_PRODUCTS}model?lob=${configuration?.COMMON_MASTER_LOB}`);
          setModels(res?.data?.data || []);

          const found = res?.data?.data?.find(item => item.page_url === brand);
          if (found) {
          setFloatSelectedModel(found);
           } else {
          setFloatSelectedModel(null); 
          }
    
      setShowBrochurePopup(true);
      // dispatch(toggleForm("brochure_form"));
    } else return null;
  };

  useEffect(() => {
    if (scrollDirection === "down") {
      setIsVisible(true);
    } else if (scrollDirection === "up") {
      setIsVisible(false);
      setActiveMobileIndex(null);
    }
  }, [scrollDirection]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setActiveMobileIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

const handleBrochureSelect = ({ model, brochureType }) => {
        let url = "";
        if (brochureType === "model") {
          url = `${configuration?.IMG_URL}${model?.brochure}`;
        } else if (brochureType === "accessory") {
          url = `${configuration?.IMG_URL}${model?.accessorybrochure}`;
        }
        if (url) window.open(url, "_blank");
        setShowBrochurePopup(false);
      };

  return (
    <>
      {/* Desktop View */}
      {!isMobile && (
        <div
          ref={sidebarRef}
          className={`floating_sidebar ${isExpanded ? "expanded" : ""} ${
            data?.theme_color === "dark"
              ? "floating_sidebar_dark"
              : "floating_sidebar_light"
          }`}
          id="floatingMenu"
          onMouseEnter={() => {
            setIsExpanded(true);
            isMini.current = false;
          }}
          onMouseLeave={() => {
            setIsExpanded(false);
            isMini.current = true;
          }}
        >
          {data &&
            data?.floating_menu_items &&
            data.floating_menu_items.map((item, index) => {
              const hasSubMenu =
                item?.subFloatingMenu && item.subFloatingMenu.length > 0;
              const submenuId = `submenu-${index}`;
              return (
                <div  className={`sidebar-item ${hasSubMenu ? "has-submenu" : ""}`} key={index}>
                  <a href="#"
                    id={`floatingMenuBtn-${index}`}
                    aria-haspopup={hasSubMenu ? "true" : undefined}
                    aria-expanded={
                      hasSubMenu
                        ? activeMobileIndex === index
                          ? "true"
                          : "false"
                        : undefined
                    }
                    aria-controls={hasSubMenu ? submenuId : undefined}
                    onClick={() => onClickHandler(item)}
                    type="button"
                  >
                    <span className="icon-text">{item?.floating_label}</span>
                    <i className="material-icons">
                      <Image
                        src={getIconUrl(item)}
                        height={20}
                        width={20}
                        alt={item?.floating_label || "floating menu icon"}
                      />
                    </i>
                  </a>

                  {hasSubMenu && (
                    <div
                      id={submenuId}
                      className={`popup ${
                        data?.theme_color === "dark"
                          ? "floating_sidebar_dark"
                          : "floating_sidebar_light"
                      }`}
                      role="menu"
                      aria-label={`${item?.floating_label} submenu`}
                    >
                      <div
                        className={`${
                          data?.theme_color === "dark"
                            ? "popup-arrow-dark"
                            : "popup-arrow-light"
                        }`}
                      />
                      <div className="popup-content-icons">
                        {item.subFloatingMenu.map((subItem, subIndex) => (
                          <div
                            className="icon-with-label"
                            key={subIndex}
                            role="menuitem"
                            tabIndex={0}
                            onClick={() => onClickHandler(subItem)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onClickHandler(subItem);
                              }
                            }}
                          >
                            <span>{subItem?.floating_label}</span>
                            <Image
                              src={getIconUrl(subItem)}
                              alt={subItem?.floating_label || "submenu icon"}
                              width={24}
                              height={24}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Mobile View */}
      {isMobile && (
        <div
          className={`floating-menu-mobile ${isVisible ? "visible" : ""} ${
            data?.theme_color === "light"
              ? "floating_sidebar_light"
              : "floating_sidebar_dark"
          }`}
        >
          {data?.floating_menu_items?.slice(0, 5).map((item, index) => {
            const hasSubMenu =
              item?.subFloatingMenu && item.subFloatingMenu.length > 0;
            const submenuId = `mobile-submenu-${index}`;
            return (
              <button
                key={index}
                id={`floatingMenuMobileBtn-${index}`}
                className={`mobile-menu-item ${
                  activeMobileIndex === index ? "active" : ""
                }`}
                aria-haspopup={hasSubMenu ? "true" : undefined}
                aria-expanded={
                  hasSubMenu
                    ? activeMobileIndex === index
                      ? "true"
                      : "false"
                    : undefined
                }
                aria-controls={hasSubMenu ? submenuId : undefined}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const screenWidth = window.innerWidth;

                  const itemCenter = rect.left + rect.width / 2;

                  let alignment = "center";
                  if (itemCenter + 100 > screenWidth) {
                    alignment = "right";
                  } else if (itemCenter - 100 < 0) {
                    alignment = "left";
                  }

                  setPopupPosition({
                    left: itemCenter,
                    alignment,
                  });

                  setActiveMobileIndex(
                    activeMobileIndex === index ? null : index
                  );
                  onClickHandler(item);
                }}
                type="button"
              >
                <Image
                  src={getIconUrl(item)}
                  alt={item?.floating_label || "floating menu icon"}
                  width={24}
                  height={24}
                />
                <span>{item?.floating_label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Mobile submenu popup */}
      {isMobile &&
        activeMobileIndex !== null &&
        data?.floating_menu_items?.[activeMobileIndex]?.subFloatingMenu
          ?.length > 0 && (
          <div
            ref={popupRef}
            id={`mobile-submenu-${activeMobileIndex}`}
            className={`popup-mobile-fixed ${
              data?.theme_color === "dark"
                ? "floating_sidebar_dark"
                : "floating_sidebar_light"
            }`}
            style={{
              left: `${popupPosition.left}px`,
              transform:
                popupPosition.alignment === "left"
                  ? "translateX(0%)"
                  : popupPosition.alignment === "right"
                  ? "translateX(-100%)"
                  : "translateX(-50%)",
            }}
            role="menu"
            aria-label={`${
              data.floating_menu_items[activeMobileIndex]?.floating_label
            } submenu`}
          >
            <div className="popup-content-icons">
              {data.floating_menu_items[activeMobileIndex].subFloatingMenu.map(
                (sub, idx) => (
                  <div
                    className="icon-with-label"
                    key={idx}
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => onClickHandler(sub)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onClickHandler(sub);
                      }
                    }}
                  >
                    <span>{sub?.floating_label}</span>
                    <Image
                      src={getIconUrl(sub)}
                      alt={sub?.floating_label || "submenu icon"}
                      width={24}
                      height={24}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        )}

          {showBrochurePopup && (
                  <BrochureSelectionPopup
                    isOpen={showBrochurePopup}
                    onClose={() => setShowBrochurePopup(false)}
                    models={models}
                    onSelect={handleBrochureSelect}
                    floatSelectedModel={floatSelectedModel}
                  />
                )}
    </>
  );
};

export default FloatingMenu;
